import {
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  Box,
  Stack,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ActivityApi, ActivityInstance } from '@api';
import { CalendarContext } from '@/contexts/CalendarContext';
import { SupervisorContext } from '@/contexts/SupervisorContext';
import { useContext, useState } from 'react';
import { isDate } from 'date-fns';
import ActivityInformation from './AcitivtyInformation';
import { Link } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getActivityColor } from '@/components/utils/CalendarFns';
import { useTheme } from '@mui/material/styles';

const ActivityDialog = ({ activity }: { activity: ActivityInstance }) => {
  const { t } = useTranslation();
  const { setCalendarDialog, refreshWeek } = useContext(CalendarContext);
  const { currentClient } = useContext(SupervisorContext);

  const { palette } = useTheme();

  const [completing, setCompleting] = useState(false);
  const [failed, setFailed] = useState(false);

  const isCompleted = isDate(activity.dateIsCompleted);
  const activityColor = getActivityColor(palette, activity.duration);

  // Återanvänder samma complete-kontrakt som InstanceContext.completeInstance:
  // ActivityApi.completeActivity → 200 vid framgång. Inget un-complete-API finns,
  // så en redan klar aktivitet visas som en inaktiverad "Klar"-indikation.
  const handleComplete = async () => {
    setFailed(false);
    setCompleting(true);
    const res = await new ActivityApi().completeActivity({
      activityInstanceId: activity.activityInstanceId,
      supervisedTag: currentClient ? currentClient.tag : undefined,
    });
    setCompleting(false);
    if (res === 200) {
      // Uppdatera veckodatan så bocken/completed-tonen slår igenom i veckovyn.
      refreshWeek();
      setCalendarDialog(null);
    } else {
      // Visa aldrig falsk framgång — behåll modalen öppen och signalera fel.
      setFailed(true);
    }
  };

  return (
    <Box sx={{ borderLeft: `12px solid ${activityColor}` }}>
      <IconButton
        aria-label={t('Close') as string}
        onClick={() => setCalendarDialog(null)}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogTitle id="scroll-dialog-title" sx={{ wordBreak: 'break-word' }}>
        {activity.activityName}
      </DialogTitle>
      <DialogContent dividers={true}>
        <ActivityInformation activity={activity} />
        <Stack spacing={1} sx={{ mt: 1 }}>
          {/* Kärnhandling: markera klar direkt i snabbtitt-modalen (alla personas).
              Temasätts via MUI success-paletten (persona-specifik). */}
          {isCompleted ? (
            <Button
              variant="outlined"
              color="success"
              disabled
              startIcon={<CheckCircleIcon />}
              sx={{ width: '100%' }}
            >
              {t('Done')}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              onClick={handleComplete}
              disabled={completing}
              startIcon={
                completing ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />
              }
              sx={{ width: '100%' }}
            >
              {t('Mark as complete')}
            </Button>
          )}
          {failed && (
            <Typography variant="caption" color="error" sx={{ textAlign: 'center' }}>
              {t('Something went wrong')}
            </Typography>
          )}
          <Button
            component={Link}
            to={`/activity/${activity.activityInstanceId}`}
            variant="text"
            sx={{ width: '100%' }}
            onClick={() => setCalendarDialog(null)}
          >
            {t('Go to activity')}
          </Button>
        </Stack>
      </DialogContent>
    </Box>
  );
};

export default ActivityDialog;
